import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  input,
  OnDestroy,
  OnInit,
  signal,
  effect,
  Renderer2,
} from '@angular/core';

import { FormBuilder, Validators, ReactiveFormsModule, FormArray, AbstractControl, FormGroup } from '@angular/forms';
import { ReplaySubject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-label';
import '@maersk-global/mds-components-core/mc-select';
import '@maersk-global/mds-components-core/mc-typeahead';
import { CodeDescription } from '../../../models/code-description.model';
import { Repair } from '../../../models/repair.model';
import { Mode } from '../../../models/mode.model';
import { ToastrService } from '../../../services/toastr-service';
import { Shop } from '../../../models/shop.model';
import { ContainerGrade } from '../../../models/container-grade.model';
import { Equipment } from '../../../models/equipment.model';
import { reapirCodeAndRepairLocationUniqueValidator } from '../../../shared-validators/repair-code-and-repair-location-unique.validator';
import { RepairLineItemForm } from '../../../form-model/cwo-repair-line-item.form.model';
import { RepairLineItemsForm } from '../../../form-model/damage-and-repair.form.model';
import { RepairLineItemComponent } from '../repair-line-item/repair-line-item.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { PartLineItemForm } from '../../../form-model/part-line-item.form.model';
import { ErrorComponent } from '../../../shared-components/error/error.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { AttachmentForm } from '../../../form-model/attachment.form.model';
import { RepairLocation } from '../../../models/repair-location.model';
import { ThirdPartyIndicator } from '../../../models/tpi.model';
import { MaintenanceOrderLineItem } from '../../../models/maintenance-order-line-item';
import { Currency } from '../../../models/currency.model';
import { RepairExclusion } from '../../../models/repair-exclusion.model';
import { mutualExclusiveRepairCodeValidator } from '../../../shared-validators/mutual-exclusive-repair-code.validator';
import { mutualExclusivePartValidator } from '../../../shared-validators/mutual-exclusive-part.validator';
import { PartExclusionRule } from '../../../models/part-exclusion-rule.model';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { repairCodePieceLimitValidator } from '../../../shared-validators/repair-code-piece-limit.validator';
import { repairCodeContractValidator } from '../../../shared-validators/repair-code-contract.validator';
import { DamageImage, InspectionDetail } from '../../../models/inpection-detail.model';
import { forEach } from 'lodash-es';
import { utils } from '../../../helper/utils';
import { v4 } from 'uuid';
@Component({
  selector: 'app-cwo-line-items',
  imports: [ReactiveFormsModule, RepairLineItemComponent, ErrorComponent, NgxSkeletonLoaderModule],
  templateUrl: './cwo-line-items.component.html',
  styleUrl: './cwo-line-items.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('removeRepairLineItem', [transition(':leave', [animate('0.2s ease-in', style({ transform: 'translateX(-100%)' }))])]),
  ],
})
export class CreateWorkOrderLineItemsComponent implements OnInit, OnDestroy, AfterContentInit {
  @Input({ required: true }) damageAndRepairForm!: FormGroup<RepairLineItemsForm>;
  @Input() existingRepairLineItems: MaintenanceOrderLineItem[] | undefined;
  mode = input.required<Mode>();
  shop = input.required<Shop>();
  inspectionDetails = input.required<InspectionDetail[]>();
  inspectionDetailsDelete = input.required<string[]>();
  inspectionDetailsWithSelectionState = signal<InspectionDetail[]>([]);
  isEditMode = input.required<boolean>();
  equipment = input.required<Equipment>();
  containerGrades = input.required<ContainerGrade[]>();
  workOrderCurrency = input.required<Currency | undefined>();
  damages = input.required<CodeDescription[]>();
  repairs = input.required<Repair[]>();
  repairExclusions = input.required<RepairExclusion[]>();
  partExclusions = input.required<PartExclusionRule[]>();
  repairLocations = input.required<RepairLocation[]>();
  tpiCodes = input.required<ThirdPartyIndicator[]>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  openDeleteLineItemConfirmation: boolean = false;
  selectedLineItemIndexForDelete: number = -1;
  addRepairLineItemButtonDisabled = signal(true);
  constructor(
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    private appTextSvc: ApplicationtTextService,
    private renderer: Renderer2,
  ) {}

  // Effect to watch for changes in inspectionDetailsDelete and update inspectionDetailsWithSelectionState
  private inspectionDetailsDeleteEffect = effect(() => {
    const deletedDetails = this.inspectionDetailsDelete();
    if (deletedDetails && deletedDetails.length > 0) {
      this.handleInspectionDetailsDelete();
    }
  });

  ngAfterContentInit(): void {
    this.damageAndRepairForm.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.damageAndRepairForm.controls.repairLineItems.clear();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  ngOnInit(): void {
    this.damageAndRepairForm.addControl('repairLineItems', this.formBuilder.nonNullable.array<FormGroup<RepairLineItemForm>>([]));
    this.damageAndRepairForm.addValidators([
      reapirCodeAndRepairLocationUniqueValidator(),
      mutualExclusiveRepairCodeValidator(this.repairExclusions()),
      mutualExclusivePartValidator(this.partExclusions()),
      repairCodePieceLimitValidator(this.repairs(), this.toastr, this.appTextSvc),
      repairCodeContractValidator(this.repairs(), this.appTextSvc),
    ]);
    if (this.existingRepairLineItems && this.existingRepairLineItems.length > 0) {
      this.existingRepairLineItems.forEach(() => this.addOneRepairLineItemByDefault(false));
    } else {
      this.addOneRepairLineItemByDefault();
    }
    this.damageAndRepairForm.statusChanges.subscribe((status) => {
      status === 'VALID' ? this.addRepairLineItemButtonDisabled.set(false) : this.addRepairLineItemButtonDisabled.set(true);
    });
    this.initializeInspectionDetailsWithSelectionState();
  }
  get repairLineItems(): FormArray<FormGroup<RepairLineItemForm>> {
    return this.damageAndRepairForm.controls.repairLineItems;
  }

  isControlInErrorState(control: AbstractControl | null): boolean | null {
    if (control?.invalid && control.touched) {
      return true;
    }
    return null;
  }

  onAddRepairLineItemClicked(setFocus: boolean = true) {
    let newLineItem = this.getSingleRepairLineItem();
    this.repairLineItems.push(newLineItem);
    if (setFocus) {
      let length = this.repairLineItems.length - 1;
      if (length > 0) {
        timer(200)
          .pipe(takeUntil(this.destroyed$))
          .subscribe(() => {
            const id = `#repair-line-item${length}-damage-code`;
            try {
              const element = this.renderer.selectRootElement(id);
              element?.focus();
            } catch {
              // Element may not exist (e.g. component destroyed before timer fires); safe to ignore.
            }
          });
      }
    }
  }
  onRemoveRepairLineItem(index: number) {
    if (this.repairLineItems.length === 1) {
      this.toastr.showToastr(this.appTextSvc.AtLeastOneRepairLineItemRequired, 'warning');
      return;
    }
    this.selectedLineItemIndexForDelete = index;
    this.openDeleteLineItemConfirmation = true;
  }
  public onDeleteLineItemConfirmationCancel() {
    this.openDeleteLineItemConfirmation = false;
    this.selectedLineItemIndexForDelete = -1;
  }
  public onDeleteLineItemConfirmationClosed() {
    this.openDeleteLineItemConfirmation = false;
    this.selectedLineItemIndexForDelete = -1;
  }
  public onDeleteLineItemConfirmation() {
    let line = this.repairLineItems.at(this.selectedLineItemIndexForDelete);
    const lineItemKey = `${line.controls.repairCode.value || ''}-${line.controls.damageCode.value || ''}-${line.controls.repairLocationCode.value || ''}`;
    this.lineItemKeyChanged(lineItemKey);
    this.repairLineItems.removeAt(this.selectedLineItemIndexForDelete);
    this.openDeleteLineItemConfirmation = false;
    this.selectedLineItemIndexForDelete = -1;
  }
  private getSingleRepairLineItem(): FormGroup<RepairLineItemForm> {
    let repairLineItem = this.formBuilder.nonNullable.group<RepairLineItemForm>({
      damageCode: this.formBuilder.nonNullable.control<string>(''),
      repairCode: this.formBuilder.nonNullable.control<string>(''),
      repairLocationCode: this.formBuilder.nonNullable.control<string>(''),
      tpiCode: this.formBuilder.nonNullable.control<string>(''),
      pieces: this.formBuilder.nonNullable.control<number>(0, Validators.pattern(/^(0|[1-9]\d*)$/)),
      materialCostPerPiece: this.formBuilder.nonNullable.control<number>(0, Validators.pattern(/^\d*\.?\d+$/)),
      manHoursPerPiece: this.formBuilder.nonNullable.control<number>(0, Validators.pattern(/^\d*\.?\d+$/)),
      itemTotalCost: this.formBuilder.nonNullable.control<string>({ value: '', disabled: true }),
      attachments: this.formBuilder.nonNullable.group<AttachmentForm>({} as AttachmentForm),
      parts: this.formBuilder.nonNullable.array<FormGroup<PartLineItemForm>>([]),
    }) as FormGroup<RepairLineItemForm>;
    return repairLineItem;
  }
  private addOneRepairLineItemByDefault(setFocus: boolean = true) {
    this.onAddRepairLineItemClicked(setFocus);
  }

  private initializeInspectionDetailsWithSelectionState() {
    this.inspectionDetailsWithSelectionState.set(this.inspectionDetails());

    if (this.isEditMode()) {
      this.existingRepairLineItems?.forEach((lineItem) => {
        forEach(lineItem.attachedImages ?? [], (attachedImage) => {
          forEach(this.inspectionDetailsWithSelectionState(), (detail) => {
            let lineItemKey: string = `${lineItem.maintenanceTypeCode ?? ''}-${lineItem.equipmentDamageCode ?? ''}-${lineItem.maintenanceRepairLocationCode ?? ''}`;
            this.updateDamageImageLinkInfoWithLineItemIdentifier(
              detail.inspectionId,
              utils.getEirImageid(attachedImage.name ?? ''),
              lineItemKey,
              this.isEditMode(),
              undefined,
              attachedImage.path ? attachedImage.path : '',
            );
          });
        });
      });
    }
  }

  get inspectionDetailsForUpload(): InspectionDetail[] {
    return [...this.inspectionDetailsWithSelectionState()];
  }

  inspectionDetailsAfterUserSelectionDoneEvent(detailsEvent: InspectionDetail[]) {
    forEach(detailsEvent, (detailEvent) => {
      detailEvent.damageImages.forEach((imageEvent) => {
        this.updateDamageImageLinkInfoWithLineItemIdentifier(
          detailEvent.inspectionId,
          imageEvent.id,
          imageEvent.linkedLineItemIdentifier,
          false,
          imageEvent.isLinked,
          imageEvent.guid ? imageEvent.guid : '',
        );
      });
    });
  }

  private updateDamageImageLinkInfoWithLineItemIdentifier(
    inspectionId: number,
    imageId: string,
    linkedLineItemIdentifier: string,
    isEditModeLinkInitialization: boolean,
    isLinked: boolean = false,
    guid: string,
  ) {
    this.inspectionDetailsWithSelectionState.update((details) => {
      const detail = details.find((d) => d.inspectionId === inspectionId);
      if (detail) {
        const existingImage = detail.damageImages.find((image) => image.id === imageId);
        if (existingImage) {
          existingImage.linkedLineItemIdentifier = linkedLineItemIdentifier;
          if (isEditModeLinkInitialization) {
            existingImage.isLinked = true;
            existingImage.guid = guid;
          } else {
            existingImage.isLinked = isLinked;
          }
        }
      }
      return details;
    });
  }

  private handleInspectionDetailsDelete() {
    forEach(this.inspectionDetailsDelete(), (imageGuid) => {
      this.inspectionDetailsWithSelectionState.update((details) => {
        const detail = details.find((d) => d.damageImages.some((img) => img.guid === imageGuid));
        if (detail) {
          detail.damageImages.forEach((image) => {
            if (image.guid === imageGuid) {
              image.isLinked = false;
              image.linkedLineItemIdentifier = '';
            }
          });
        }
        return details;
      });
    });
  }

  lineItemKeyChanged(oldLineItemKey: string) {
    this.inspectionDetailsWithSelectionState.update((details) => {
      details.forEach((detail) => {
        if (detail && detail.damageImages) {
          detail.damageImages.forEach((image) => {
            if (image.linkedLineItemIdentifier === oldLineItemKey) {
              image.linkedLineItemIdentifier = '';
              image.isLinked = false;
            }
          });
        }
      });
      return details;
    });
  }
}
